'use client';

import { useEffect, useRef } from 'react';


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      const blobs = heroRef.current.querySelectorAll<HTMLElement>('[data-parallax]');
      blobs.forEach((blob) => {
        const speed = parseFloat(blob.dataset.parallax || '1');
        blob.style.transform = `translate(${x * speed * 40}px, ${y * speed * 40}px)`;
      });
    };
    const el = heroRef.current;
    el?.addEventListener('mousemove', handleMouse);
    return () => el?.removeEventListener('mousemove', handleMouse);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden animated-bg pt-24"
    >
      {/* Noise overlay */}
      <div className="noise-overlay" />

      {/* Background blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          data-parallax="0.4"
          className="blob-1 absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-[120px]"
          style={{ background: 'radial-gradient(circle, rgba(255,107,26,0.18) 0%, transparent 70%)' }}
        />
        <div
          data-parallax="0.6"
          className="blob-2 absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[100px]"
          style={{ background: 'radial-gradient(circle, rgba(255,179,71,0.12) 0%, transparent 70%)' }}
        />
        <div
          data-parallax="0.3"
          className="blob-3 absolute top-1/2 right-1/3 w-[400px] h-[400px] rounded-full blur-[140px]"
          style={{ background: 'radial-gradient(circle, rgba(255,107,26,0.08) 0%, transparent 70%)' }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-warm text-xs font-semibold tracking-widest uppercase mb-8">
            <span className="w-2 h-2 rounded-full bg-citrus animate-ping inline-block" />
            <span className="text-amber">New 2026 Formula · Now Available</span>
          </div>
        </div>

        {/* Giant headline */}
        <div className="text-center relative">
          <h1 className="font-fraunces font-black leading-[0.85] tracking-tight select-none">
            <span
              className="block text-[clamp(80px,14vw,180px)] gradient-text-night"
              style={{ letterSpacing: '-0.04em' }}
            >
              NIGHT
            </span>
            <span
              className="block text-[clamp(80px,14vw,180px)] gradient-text-citrus italic"
              style={{ letterSpacing: '-0.04em' }}
            >
              BURN
            </span>
            <span
              className="block text-[clamp(40px,6vw,80px)] text-cream/70 font-light not-italic tracking-wide mt-2"
              style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.15em' }}
            >
              ORANGE JUICE
            </span>
          </h1>

          {/* Sub-tagline */}
          <p className="mt-8 text-lg md:text-xl text-cream-muted max-w-xl mx-auto leading-relaxed font-light">
            One glass before bed. Your body burns fat for{' '}
            <span className="text-amber font-semibold">8 hours straight</span> while you sleep.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="#order"
              className="group relative inline-flex items-center gap-3 bg-citrus hover:bg-citrus-light text-white font-bold text-base px-8 py-4 rounded-full transition-all duration-300 hover:shadow-2xl hover:shadow-citrus/40 hover:-translate-y-1 pulse-glow overflow-hidden"
            >
              <div className="border-beam-wrapper">
                <div className="border-beam-inner" style={{ padding: '1px', WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }} />
              </div>
              <span className="relative z-10">Start Burning Tonight</span>
              <svg className="relative z-10 group-hover:translate-x-1 transition-transform" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <a
              href="#science"
              className="inline-flex items-center gap-2 text-cream-muted hover:text-cream font-medium text-sm border border-cream/10 hover:border-cream/25 px-6 py-4 rounded-full transition-all duration-300"
            >
              See the science
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </a>
          </div>
        </div>

        {/* Floating UI cards */}
        {/* Card 1: Top Left — Metabolism active */}
        <div className="hidden lg:block absolute left-[3%] top-[28%] float-card-1">
          <div className="glass-card rounded-2xl p-4 w-64 shadow-2xl shadow-black/40">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs font-semibold text-muted uppercase tracking-wider">Fat Burn Active</span>
            </div>
            <div className="flex items-end gap-2 mb-3">
              <span className="text-3xl font-bold font-fraunces text-citrus">+340%</span>
              <span className="text-xs text-muted mb-1">metabolic rate</span>
            </div>
            {/* Mini bar chart */}
            <div className="flex items-end gap-1 h-10">
              {[30, 45, 55, 70, 90, 100, 95, 85].map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{
                    height: `${h}%`,
                    background: i >= 4
                      ? 'linear-gradient(180deg, #FF6B1A, #FFB347)'
                      : 'rgba(255,255,255,0.08)',
                  }}
                />
              ))}
            </div>
            <p className="text-[10px] text-muted mt-2">While you sleep 10pm–6am</p>
          </div>
        </div>

        {/* Card 2: Top Right — Vitamin C */}
        <div className="hidden lg:block absolute right-[3%] top-[22%] float-card-2">
          <div className="glass-card rounded-2xl p-4 w-56 shadow-2xl shadow-black/40">
            <div className="w-10 h-10 rounded-xl bg-citrus/15 flex items-center justify-center mb-3">
              <span className="text-xl">🍊</span>
            </div>
            <p className="text-sm font-semibold text-cream mb-1">Vitamin C Complex</p>
            <p className="text-xs text-muted leading-relaxed">Activates lipolysis enzymes that break down fat cells overnight.</p>
            <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full w-4/5 rounded-full" style={{ background: 'linear-gradient(90deg, #FF6B1A, #FFB347)' }} />
            </div>
            <p className="text-[10px] text-muted mt-1">80% daily value per glass</p>
          </div>
        </div>

        {/* Card 3: Bottom Left — Sleep quality */}
        <div className="hidden lg:block absolute left-[5%] bottom-[12%] float-card-3">
          <div className="glass-card rounded-2xl p-4 w-52 shadow-2xl shadow-black/40">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-muted uppercase tracking-wider">Sleep Quality</span>
              <span className="text-xs text-green-400 font-bold">+28%</span>
            </div>
            {/* Moon phase icons */}
            <div className="flex gap-2 mb-2">
              {['🌑', '🌒', '🌓', '🌔', '🌕'].map((moon, i) => (
                <span key={i} className="text-lg opacity-90">{moon}</span>
              ))}
            </div>
            <p className="text-[11px] text-muted">Deep sleep cycles extended</p>
          </div>
        </div>

        {/* Card 4: Bottom Right — Calories */}
        <div className="hidden lg:block absolute right-[5%] bottom-[10%] float-card-4">
          <div className="glass-card-warm rounded-2xl p-4 w-60 shadow-2xl shadow-black/40">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber/15 flex items-center justify-center text-amber text-lg">🔥</div>
              <div>
                <p className="text-sm font-bold text-cream">420 cal</p>
                <p className="text-[10px] text-muted">burned overnight</p>
              </div>
            </div>
            <div className="text-[11px] text-muted border-t border-white/5 pt-2 mt-2">
              Avg. across 847 verified customers
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted">
        <span className="text-xs uppercase tracking-widest font-medium">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-citrus/50 to-transparent animate-pulse" />
      </div>
    </section>
  );
}